import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import {
  AlertTriangleIcon,
  ExternalLinkIcon,
  GripVerticalIcon,
  HardDriveIcon,
  XIcon,
} from 'lucide-react'
import { Boton } from '@/components/ui/Boton'
import { cn } from '@/lib/cn'
import {
  useElegirDeDrive,
  type ArchivoDeDrive,
  type EsperaArchivo,
} from '@/lib/drive/useElegirDeDrive'
import { useEffect } from 'react'
import { precargarPicker, type ArchivoElegido } from '@/lib/drive/picker'
import type { ResultadoValidacion } from '@/lib/drive/validar'
import { enlaceDrive } from '@/lib/schemas/publicaciones'

/**
 * "Elegir de Drive" (Fase 6): abre el selector acotado a la carpeta del
 * cliente y devuelve lo elegido ya validado. Si falta el permiso de Drive,
 * ofrece darlo; si el ambiente no tiene el selector, no se muestra y queda
 * pegar el enlace a mano.
 */
export function BotonDrive({
  carpetaId,
  multiple,
  espera,
  titulo,
  etiqueta = 'Elegir de Drive',
  alElegir,
}: {
  carpetaId: string | null
  multiple: boolean
  espera: EsperaArchivo
  titulo: string
  etiqueta?: string
  alElegir: (archivos: ArchivoDeDrive[]) => void
}) {
  const drive = useElegirDeDrive()
  useEffect(precargarPicker, [])
  if (!drive.disponible) return null

  return (
    <div className="flex flex-col gap-2">
      <Boton
        type="button"
        variante="secundario"
        tamano="chico"
        className="self-start"
        disabled={drive.estado === 'abriendo' || drive.estado === 'leyendo'}
        onClick={() => {
          void drive.elegir({ carpetaId, multiple, espera, titulo }).then((lista) => {
            if (lista.length > 0) alElegir(lista)
          })
        }}
      >
        <HardDriveIcon className="size-4" aria-hidden />
        {drive.estado === 'leyendo'
          ? 'Leyendo los archivos…'
          : drive.estado === 'abriendo'
            ? 'Abriendo Drive…'
            : etiqueta}
      </Boton>
      {!carpetaId && (
        <p className="text-xs text-tinta-suave">
          Este cliente no tiene carpeta de Drive elegida: el selector abre tu Drive
          entero. Elegila en Administración.
        </p>
      )}
      {drive.estado === 'permiso' && (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-2xl bg-cambios px-4 py-3 text-sm text-cambios-tinta"
        >
          <p>
            Para elegir de Drive, Google te pide permiso para Drive (la primera vez, o si
            el permiso anterior venció): el portal sólo puede ver los archivos que elijas.
            Vas a volver a esta pantalla; lo que no guardaste en este formulario se
            pierde.
          </p>
          <Boton
            type="button"
            tamano="chico"
            className="self-start"
            onClick={() => {
              void drive.pedirPermiso()
            }}
          >
            Dar permiso de Drive
          </Boton>
        </div>
      )}
      {drive.error && (
        <p role="alert" className="text-xs text-nuevo">
          {drive.error}
        </p>
      )}
    </div>
  )
}

/** El aviso de permiso y el error, comunes a los dos botones. */
function EstadoDrive({ drive }: { drive: ReturnType<typeof useElegirDeDrive> }) {
  return (
    <>
      {drive.estado === 'permiso' && (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-2xl bg-cambios px-4 py-3 text-sm text-cambios-tinta"
        >
          <p>
            Para elegir de Drive, Google te pide permiso para Drive (la primera vez, o si
            el permiso anterior venció): el portal sólo puede ver lo que elijas. Vas a
            volver a esta pantalla; lo que no guardaste en este formulario se pierde.
          </p>
          <Boton
            type="button"
            tamano="chico"
            className="self-start"
            onClick={() => {
              void drive.pedirPermiso()
            }}
          >
            Dar permiso de Drive
          </Boton>
        </div>
      )}
      {drive.error && (
        <p role="alert" className="text-xs text-nuevo">
          {drive.error}
        </p>
      )}
    </>
  )
}

/**
 * "Elegir de Drive" para una carpeta o para documentos que se guardan como
 * enlace (Fase 6): la carpeta del cliente, el documento de una planificación,
 * los enlaces de un recurso.
 */
export function BotonDriveEnlace({
  carpetaId,
  modo,
  multiple,
  tipos,
  titulo,
  etiqueta,
  alElegir,
}: {
  carpetaId: string | null
  modo: 'archivos' | 'carpeta'
  multiple: boolean
  tipos?: string
  titulo: string
  etiqueta: string
  alElegir: (elegidos: ArchivoElegido[]) => void
}) {
  const drive = useElegirDeDrive()
  useEffect(precargarPicker, [])
  if (!drive.disponible) return null
  return (
    <div className="flex flex-col gap-2">
      <Boton
        type="button"
        variante="secundario"
        tamano="chico"
        className="self-start"
        disabled={drive.estado === 'abriendo'}
        onClick={() => {
          void drive
            .elegirEnlaces({
              carpetaId,
              modo,
              multiple,
              titulo,
              ...(tipos ? { tipos } : {}),
            })
            .then((lista) => {
              if (lista.length > 0) alElegir(lista)
            })
        }}
      >
        <HardDriveIcon className="size-4" aria-hidden />
        {drive.estado === 'abriendo' ? 'Abriendo Drive…' : etiqueta}
      </Boton>
      <EstadoDrive drive={drive} />
    </div>
  )
}

export interface InfoArchivo {
  nombre?: string
  validacion?: ResultadoValidacion
}

/**
 * La lista de archivos del campo, en orden. En un carrusel se reordena
 * arrastrando (o con el teclado) y cada lámina se puede quitar. Muestra el
 * rechazo o el aviso de cada archivo validado.
 */
export function ListaArchivos({
  ids,
  info,
  carrusel,
  alReordenar,
  alQuitar,
  privados,
}: {
  ids: string[]
  info: Record<string, InfoArchivo | undefined>
  carrusel: boolean
  alReordenar: (ids: string[]) => void
  alQuitar: (id: string) => void
  /** Ids que Drive no muestra sin sesión (sólo para enlaces pegados). */
  privados: Set<string>
}) {
  const sensores = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  if (ids.length === 0) return null

  const avisos = ids
    .map((id, i) => ({ id, i, v: info[id]?.validacion }))
    .filter((x) => x.v && x.v.estado !== 'ok')

  return (
    <div className="flex flex-col gap-2">
      <DndContext
        sensors={sensores}
        collisionDetection={closestCenter}
        onDragEnd={(e: DragEndEvent) => {
          const desde = ids.indexOf(String(e.active.id))
          const hasta = e.over ? ids.indexOf(String(e.over.id)) : -1
          if (desde >= 0 && hasta >= 0 && desde !== hasta) {
            alReordenar(arrayMove(ids, desde, hasta))
          }
        }}
      >
        <SortableContext items={ids} strategy={horizontalListSortingStrategy}>
          <ul className="flex flex-wrap gap-2" aria-label="Archivos elegidos">
            {ids.map((id, i) => (
              <Ficha
                key={id}
                id={id}
                n={i + 1}
                carrusel={carrusel}
                info={info[id]}
                privado={privados.has(id)}
                alQuitar={() => {
                  alQuitar(id)
                }}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      {carrusel && ids.length > 1 && (
        <p className="text-xs text-tinta-suave">
          Arrastrá las láminas para cambiar el orden.
        </p>
      )}
      {avisos.map(({ id, i, v }) =>
        v && v.estado !== 'ok' ? (
          <p
            key={id}
            role={v.estado === 'rechazo' ? 'alert' : 'status'}
            data-validacion={v.estado}
            className={cn(
              'flex gap-2 rounded-xl px-3.5 py-2.5 text-sm',
              v.estado === 'rechazo'
                ? 'bg-nuevo/10 text-nuevo'
                : 'bg-cambios text-cambios-tinta',
            )}
          >
            <AlertTriangleIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              <strong>{carrusel ? `Lámina ${String(i + 1)}` : 'El archivo'}:</strong>{' '}
              {v.mensaje}
            </span>
          </p>
        ) : null,
      )}
    </div>
  )
}

function Ficha({
  id,
  n,
  carrusel,
  info,
  privado,
  alQuitar,
}: {
  id: string
  n: number
  carrusel: boolean
  info: InfoArchivo | undefined
  privado: boolean
  alQuitar: () => void
}) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } =
    useSortable({ id, disabled: !carrusel })
  const v = info?.validacion
  const color =
    v?.estado === 'rechazo'
      ? 'bg-nuevo/10 text-nuevo'
      : v?.estado === 'aviso' || privado
        ? 'bg-cambios text-cambios-tinta'
        : 'bg-lienzo'
  return (
    <li
      ref={setNodeRef}
      style={{
        transform: transform
          ? `translate3d(${String(transform.x)}px, ${String(transform.y)}px, 0)`
          : undefined,
        transition,
      }}
      data-archivo={id}
      data-drive-privado={privado ? 'si' : 'no'}
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-full py-1 pr-1 pl-2 text-xs',
        color,
        isDragging && 'z-10 shadow-tarjeta',
      )}
    >
      {carrusel && (
        <button
          type="button"
          aria-label={`Mover la lámina ${String(n)}`}
          className="cursor-grab touch-none rounded p-0.5 hover:bg-borde"
          {...attributes}
          {...listeners}
        >
          <GripVerticalIcon className="size-3.5" aria-hidden />
        </button>
      )}
      <a
        href={enlaceDrive(id)}
        target="_blank"
        rel="noreferrer"
        className="inline-flex min-w-0 items-center gap-1 text-hoy hover:underline"
      >
        <span className="truncate">
          {carrusel ? `Lámina ${String(n)}` : 'Archivo'}
          {info?.nombre ? ` · ${info.nombre}` : ''}
        </span>
        <ExternalLinkIcon className="size-3 shrink-0" aria-hidden />
      </a>
      <button
        type="button"
        aria-label={carrusel ? `Quitar la lámina ${String(n)}` : 'Quitar el archivo'}
        onClick={alQuitar}
        className="rounded-full p-1 hover:bg-borde"
      >
        <XIcon className="size-3" aria-hidden />
      </button>
    </li>
  )
}
